import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

type CenterScreenModal = {
  children: React.ReactNode,
  isOpen: boolean
  onClose?: () => void
}

const closeIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293z"/>
  </svg>
)

const ModalOverlay = ({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) => (
  <div onClick={onClick} className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
    {children}
  </div>
);
const ModalContent = ({ children, onClick }: { children: React.ReactNode; onClick?: (e: React.MouseEvent) => void }) => (
  <div onClick={onClick} className="bg-white p-8 rounded-lg relative shadow-md max-h-[85%] overflow-auto min-w-[350px]">
    {children}
  </div>
);
const ModalCloseButton = ({ onClick, children }: { onClick?: () => void; children: React.ReactNode }) => (
  <button type="button" onClick={onClick} className="absolute top-2.5 right-2.5 w-9 h-9 text-blue-500 bg-none border-none cursor-pointer z-10">
    {children}
  </button>
);

export const CenterScreenModal = ({ children, isOpen, onClose }: CenterScreenModal) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    containerRef.current = document.createElement('div');
    document.body.appendChild(containerRef.current);

    return () => {
      if (containerRef.current) {
        document.body.removeChild(containerRef.current);
      }
    };
  }, [])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return createPortal(
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalCloseButton onClick={onClose}>{closeIcon}</ModalCloseButton>
        {children}
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};